// Where a shop's storefront answers. Used by the storefront-preview endpoint
// (server) and by the POS views that link to or frame the storefront (client), so
// the product Preview button, the Storefront tab, the colours preview and the
// order's page link agree.
//
// The host comes from the shop's settings.shopdomain (on johkutest, tervarumpu's is
// storefronttest.jalusta.com), else {shop}.<storefront domain>. The storefront domain
// is runtime config: NUXT_PUBLIC_STOREFRONT_DOMAIN, default johku.com.

// Same host rule the backend uses before it stores settings.shopdomain.
export const SHOP_DOMAIN_RE = /^((xn--)?[a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i
export const DEFAULT_STOREFRONT_DOMAIN = 'johku.com'

function cleanDomain (shopdomain: unknown): string {
  const domain = typeof shopdomain === 'string' ? shopdomain.trim().toLowerCase() : ''
  return domain && SHOP_DOMAIN_RE.test(domain) ? domain : ''
}

function platform (storefrontDomain: unknown): string {
  return cleanDomain(storefrontDomain) || DEFAULT_STOREFRONT_DOMAIN
}

function bareHost (host: string): string {
  return String(host || '').trim().toLowerCase().replace(/:\d+$/, '')
}

function isUnder (host: string, domain: string): boolean {
  const name = bareHost(host)
  return name === domain || name.endsWith('.' + domain)
}

// Last two labels: enough for the hosts in use (johku.com, jalusta.com, shop domains like tervarumpu.fi).
function siteOf (host: string): string {
  return bareHost(host).split('.').slice(-2).join('.')
}

// The shop's own domain, else {shop}.<storefront domain>.
export function storefrontOrigin (shopId: string, shopdomain: unknown, storefrontDomain?: unknown): string {
  const domain = cleanDomain(shopdomain)
  return 'https://' + (domain || shopId.toLowerCase() + '.' + platform(storefrontDomain))
}

// For a frame inside the POS. The staff login reaches the storefront as a
// SameSite=Lax cookie, which browsers only keep in a frame on the same site as
// the POS page around it.
// - The shop's own domain when it is on the POS's site (johkutest: apptest.jalusta.com
//   frames storefronttest.jalusta.com), or when the POS isn't on the storefront domain.
// - Otherwise, with the POS on the storefront domain (app.johku.com): {shop}.johku.com,
//   with johkudomain so the storefront doesn't move to the shop's own domain.
export function storefrontFrameTarget (shopId: string, shopdomain: unknown, appHost: string, storefrontDomain?: unknown): { origin: string, johkuDomain: boolean } {
  const domain = cleanDomain(shopdomain)
  const base = platform(storefrontDomain)
  const platformOrigin = 'https://' + shopId.toLowerCase() + '.' + base
  if (!domain) {
    return { origin: platformOrigin, johkuDomain: false }
  }
  if (siteOf(domain) === siteOf(appHost) || !isUnder(appHost, base)) {
    return { origin: 'https://' + domain, johkuDomain: false }
  }
  return { origin: platformOrigin, johkuDomain: true }
}
